import http from "services/http/http";
import { sentryLogException, sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";
import type ApiException from "types/api";

export type LeumitTokenValidationOutcome = "valid" | "invalid" | "error";

type LeumitTokenValidationMode = "lambda" | "direct";

type LeumitTokenLambdaResponse = {
  valid: boolean;
};

const TOKEN_HASH_PREFIX_LENGTH = 8;

const getValidationMode = (): LeumitTokenValidationMode => {
  const mode = import.meta.env.VITE_LEUMIT_TOKEN_VALIDATION_MODE;

  if (mode === "lambda" || mode === "direct") {
    return mode;
  }

  return "direct";
};

const getTokenHashPrefix = async (token: string): Promise<string | null> => {
  if (!token || typeof crypto === "undefined" || !crypto.subtle) {
    return null;
  }

  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(token);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hexHash = hashArray
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");

    return hexHash.slice(0, TOKEN_HASH_PREFIX_LENGTH);
  } catch {
    return null;
  }
};

const resolveMockOutcome = (): LeumitTokenValidationOutcome | null => {
  const mockValue = import.meta.env.VITE_MOCK_LEUMIT_TOKEN_VALIDATION?.trim().toLowerCase();

  if (mockValue === "valid" || mockValue === "invalid" || mockValue === "error") {
    return mockValue;
  }

  return null;
};

const logValidationOutcome = async (
  outcome: LeumitTokenValidationOutcome,
  token: string,
  mode: LeumitTokenValidationMode,
): Promise<void> => {
  const tokenHashPrefix = await getTokenHashPrefix(token);

  await sentryLogMessage({
    message: "Leumit token validation completed",
    level: outcome === "valid" ? "info" : "warning",
    tags: {
      component: "leumitTokenValidation",
      category: "token_validation",
      event_type: SENTRY_EVENT_TYPES.LEUMIT_TOKEN_VALIDATION,
      validation_mode: mode,
      validation_outcome: outcome,
    },
    extra: {
      tokenLength: token.length,
      ...(tokenHashPrefix ? { tokenHashPrefix } : {}),
      timestamp: new Date().toISOString(),
    },
  });
};

const logValidationFailure = async (
  error: unknown,
  token: string,
  mode: LeumitTokenValidationMode,
): Promise<void> => {
  const tokenHashPrefix = await getTokenHashPrefix(token);
  const apiError = error as ApiException;

  sentryLogException({
    error,
    tags: {
      component: "leumitTokenValidation",
      category: "token_validation",
      event_type: SENTRY_EVENT_TYPES.LEUMIT_TOKEN_VALIDATION,
      validation_mode: mode,
    },
    extra: {
      tokenLength: token.length,
      ...(tokenHashPrefix ? { tokenHashPrefix } : {}),
      statusCode: apiError?.statusCode ?? "unknown",
      timestamp: new Date().toISOString(),
    },
  });
};

const validateViaLambda = async (token: string): Promise<LeumitTokenValidationOutcome> => {
  const authKey = import.meta.env.VITE_AUTH_KEY;

  if (!authKey) {
    throw new Error("VITE_AUTH_KEY is not configured");
  }

  const response = await http.request<LeumitTokenLambdaResponse>({
    key: "validateLeumitTokenLambda",
    data: { token },
    extraHeader: {
      "Content-Type": "application/json",
      "X-API-Key": authKey,
    },
  });

  return response.valid ? "valid" : "invalid";
};

const validateViaDirect = async (token: string): Promise<LeumitTokenValidationOutcome> => {
  await http.request({
    key: "validateLeumitTokenDirect",
    params: { token },
  });

  return "valid";
};

export const validateLeumitToken = async (
  token: string,
): Promise<LeumitTokenValidationOutcome> => {
  const mockOutcome = resolveMockOutcome();

  if (mockOutcome) {
    await logValidationOutcome(mockOutcome, token, getValidationMode());
    return mockOutcome;
  }

  const validationUrl = import.meta.env.VITE_LEUMIT_TOKEN_VALIDATION_URL?.trim();
  const mode = getValidationMode();

  if (!validationUrl) {
    const configError = new Error("VITE_LEUMIT_TOKEN_VALIDATION_URL is not configured");

    await logValidationFailure(configError, token, mode);
    return "error";
  }

  try {
    const outcome =
      mode === "lambda"
        ? await validateViaLambda(token)
        : await validateViaDirect(token);

    await logValidationOutcome(outcome, token, mode);
    return outcome;
  } catch (error: unknown) {
    const apiError = error as ApiException;

    if (apiError?.statusCode === 400) {
      await logValidationOutcome("invalid", token, mode);
      return "invalid";
    }

    await logValidationFailure(error, token, mode);
    return "error";
  }
};
